import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import intlShape from '@/shared/utils/intlShape';
import OkCancelModal from '@/shared/components/OkCancelModal';
import classNames from 'classnames';
import api from '@/shared/api';
import config from '@/config';
import moment from 'moment';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import { dynamicSort } from '@/shared/utils/dynamicSort';
import messages from '../invoiceStreamMessages';
import dateFormatService from '@/shared/services/DateFormatService';
import {
  isBeforeToday,
  isBeforeMinimum,
  isBeforeLastYear,
  isBeforeTomorrow,
} from '@/shared/utils/validationHelpers';
import DatePicker from '@/shared/components/input/DatePicker';

const getInvoiceType = (invoicingType, fixed) => {
  switch (invoicingType) {
    case 0:
      return 4;
    case 1:
      return 1;
    case 2:
      return fixed ? 2 : 3;
    default:
      return 4;
  }
};

export default class ReprocessInvoiceStreamScheduleModal extends Component {
  state = {
    invoiceSchedules: [],
    invoiceSchedule: null,
    showValidation: null,
    invoiceScheduleLineId: null,
    invoiceCreationDate: null,
    isTaskflowBilling: false,
  };

  static propTypes = {
    show: PropTypes.bool.isRequired,
    intl: intlShape.isRequired,
    onSave: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    exampleAmount: PropTypes.number,
  };

  componentDidMount() {
    const { intl } = this.props;
    this.loadInvoiceSchedules();
  }

  loadInvoiceSchedules = () => {
    const { intl, invoiceScheduleId } = this.props;
    api.base.get(`${config.appUrls.api}/api/InvoiceSchedule/Full`).then(({ data }) => {
      const invoiceSchedules = data
        .map(x => ({
          ...x,
          label: intl.formatMessage(listItemMsg('InvoiceSchedule', x.id, x.name)),
          lines: (x.lines || [])
            .map(l => {
              let previousDate = moment.utc(l.invoiceCreationDate).year(moment.utc().year());
              if (moment.utc().isBefore(previousDate)) previousDate = previousDate.subtract(1, 'y');

              const lineTypeLabel = intl.formatMessage(
                listItemMsg('InvoiceType', getInvoiceType(x.invoicingType, l.fixed)),
              );
              let label = '';
              let isTaskflowBilling = false;
              if (l.mainTaskTemplateId && l.subtaskTemplateId) {
                isTaskflowBilling = true;
                const mainTaskTemplate = intl.formatMessage(
                  listItemMsg('TaskTemplate', l.mainTaskTemplateId),
                );
                const subTaskTemplate = intl.formatMessage(
                  listItemMsg('TaskTemplate', l.subtaskTemplateId),
                );
                label = `${mainTaskTemplate} - ${subTaskTemplate} - ${lineTypeLabel}`;
                previousDate = moment.utc();
              } else {
                label = `${dateFormatService.formatDate(previousDate)} - ${lineTypeLabel}`;
              }
              return {
                ...l,
                previousDate,
                previousDateSort: moment(previousDate).format('x'),
                lineTypeLabel,
                label,
                isTaskflowBilling,
              };
            })
            .sort(dynamicSort('-previousDateSort')),
        }))
        .filter(x => x.inUse && x.lines.length > 0)
        .sort(dynamicSort('label'));

      const invoiceSchedule = invoiceScheduleId
        ? invoiceSchedules.find(x => x.id === invoiceScheduleId)
        : null;
      const invoiceScheduleLine = invoiceSchedule ? invoiceSchedule.lines[0] : null;

      this.setState({
        fixedInvoiceSchedule: !!invoiceScheduleId && !!invoiceSchedule,
        invoiceSchedules,
        invoiceSchedule,
        invoiceScheduleLineId: invoiceScheduleLine?.id,
        invoiceCreationDate: invoiceScheduleLine?.previousDate,
        isTaskflowBilling: invoiceScheduleLine?.isTaskflowBilling ?? false,
      });
    });
  };
  invoiceScheduleChanged = e => {
    const id = e.target.value;
    this.setState(({ invoiceSchedules }) => {
      const invoiceSchedule = id ? invoiceSchedules.find(x => x.id === id) : null;
      const invoiceScheduleLine = invoiceSchedule ? invoiceSchedule.lines[0] : null;
      return {
        invoiceSchedule,
        invoiceScheduleLineId: invoiceScheduleLine?.id,
        invoiceCreationDate: invoiceScheduleLine?.previousDate,
        isTaskflowBilling: invoiceScheduleLine?.isTaskflowBilling ?? false,
      };
    });
  };
  invoiceScheduleLineChanged = e => {
    const id = e.target.value;
    this.setState(({ invoiceSchedule }) => {
      const invoiceScheduleLine =
        invoiceSchedule && id ? invoiceSchedule.lines.find(x => x.id === id) : null;
      return {
        invoiceScheduleLineId: invoiceScheduleLine?.id,
        invoiceCreationDate: invoiceScheduleLine?.previousDate,
        isTaskflowBilling: invoiceScheduleLine?.isTaskflowBilling ?? false,
      };
    });
  };
  isFormInvalid = () => {
    const { invoiceSchedule, invoiceScheduleLineId, invoiceCreationDate } = this.state;
    if (!invoiceSchedule) return true;
    if (!invoiceScheduleLineId) return true;
    if (!invoiceCreationDate) return true;
    if (isBeforeLastYear(invoiceCreationDate)) return true;
    if (!isBeforeTomorrow(invoiceCreationDate)) return true;

    return false;
  };
  onSave = () => {
    const { onSave } = this.props;
    const { invoiceSchedule, invoiceScheduleLineId, invoiceCreationDate } = this.state;

    if (this.isFormInvalid()) return;

    return onSave({
      invoiceScheduleId: invoiceSchedule.id,
      invoiceScheduleLineId,
      invoiceCreationDate,
    });
  };
  render() {
    const { invoiceScheduleId, intl, show, onClose } = this.props;
    const {
      fixedInvoiceSchedule,
      invoiceSchedules,
      invoiceSchedule,
      invoiceScheduleLineId,
      invoiceCreationDate,
      dateFocused,
      showValidation,
      isTaskflowBilling,
    } = this.state;

    return (
      <OkCancelModal
        title={intl.formatMessage(messages.ReprocessInvoiceStreamSchedules)}
        show={show}
        onClose={onClose}
        disabled={this.isFormInvalid()}
        onSave={() => this.onSave()}
        onHoverSave={() => this.setState({ showValidation: true })}
      >
        <div className="form-horizontal">
          <div
            className={classNames('form-group', {
              'has-error': showValidation && !invoiceSchedule,
            })}
          >
            <label className="control-label col-sm-4">
              {intl.formatMessage(messages.InvoiceSchedule)}
            </label>
            <div className="col-sm-8">
              {invoiceSchedules && !fixedInvoiceSchedule ? (
                <select
                  onChange={this.invoiceScheduleChanged}
                  className="form-control"
                  value={invoiceSchedule?.id || ''}
                >
                  {!invoiceScheduleId && !invoiceSchedule && <option></option>}
                  {invoiceSchedules.map(schedule => (
                    <option key={schedule.id} value={schedule.id}>
                      {schedule.label}
                    </option>
                  ))}
                </select>
              ) : (
                invoiceSchedule?.label
              )}
            </div>
          </div>
          {invoiceSchedule ? (
            <div
              className={classNames('form-group', {
                'has-error': showValidation && !invoiceScheduleLineId,
              })}
            >
              <label className="control-label col-sm-4">
                {intl.formatMessage(messages.InvoiceScheduleLine)}
              </label>
              <div className="col-sm-8">
                <select
                  onChange={this.invoiceScheduleLineChanged}
                  className="form-control"
                  value={invoiceScheduleLineId || ''}
                >
                  {invoiceSchedule.lines.map(line => (
                    <option key={line.id} value={line.id}>
                      {line.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ) : null}

          {invoiceSchedule ? (
            <div
              className={classNames('form-group', {
                'has-error':
                  showValidation &&
                  (!invoiceCreationDate ||
                    isBeforeLastYear(invoiceCreationDate) ||
                    !isBeforeTomorrow(invoiceCreationDate)),
              })}
            >
              <label className="control-label col-sm-4">
                {intl.formatMessage(
                  isTaskflowBilling
                    ? messages.TaskflowInvoiceCreationDateFrom
                    : messages.InvoiceCreationDate,
                )}
              </label>
              <div className="col-sm-8">
                <DatePicker
                  placeholder={intl.formatMessage(
                    isTaskflowBilling
                      ? messages.TaskflowInvoiceCreationDateFrom
                      : messages.InvoiceCreationDate,
                  )}
                  date={invoiceCreationDate}
                  onDateChange={date =>
                    this.setState({
                      invoiceCreationDate: date,
                    })
                  }
                  focused={dateFocused}
                  onFocusChange={({ focused }) => this.setState({ dateFocused: focused })}
                  id="InvoiceCreationDate"
                  required
                  block
                  small
                  isOutsideRange={date => isBeforeLastYear(date) || !isBeforeTomorrow(date)}
                />
              </div>
            </div>
          ) : null}

          {isTaskflowBilling ? (
            <div className="alert alert-info">
              {intl.formatMessage(messages.TaskflowBillingLastLineInfo, {
                date: invoiceCreationDate ? dateFormatService.formatDate(invoiceCreationDate) : '',
              })}
            </div>
          ) : null}
        </div>
      </OkCancelModal>
    );
  }
}
